"use client";

import React, { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Award, CalendarDays, Phone, QrCode, User, UserRound } from 'lucide-react';
import SEO from '../../components/SEO';
import OptimizedImage from '../../components/OptimizedImage';
import { usePrerender } from '../../hooks/usePrerender';
import { getCaseDetailBySlug } from '../../data/caseDetails';
import CaseStudyContent from './CaseStudyContent';
import wechatImg from '../../assets/img/team/wechat.png';
import teamImg1 from '../../assets/img/team/01.png';

const CaseImageDetailPage: React.FC = () => {
  usePrerender();
  const { slug } = useParams<{ slug: string }>();
  const detail = getCaseDetailBySlug(slug);
  const [imageLoaded, setImageLoaded] = useState(false);

  if (!detail) {
    return <Navigate to="/cases" replace />;
  }

  const seoDescription = detail.content?.summary
    ?? `${detail.title}完整案例，展示企业GEO优化过程、数据指标与最终效果。`;

  const structuredData = detail.content
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            headline: detail.title,
            description: detail.content.summary,
            image: detail.detailImage,
            datePublished: detail.publishedAt,
            dateModified: detail.publishedAt,
            articleSection: 'GEO成功案例',
            keywords: detail.content.keywords,
            author: {
              '@type': 'Organization',
              name: detail.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '前排科技（武汉）有限公司',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `/cases/${detail.slug}`,
            },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: 'GEO成功案例', item: '/cases' },
              { '@type': 'ListItem', position: 3, name: detail.title },
            ],
          },
        ],
      }
    : undefined;

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      <SEO
        title={`${detail.title} | 前排科技GEO`}
        description={seoDescription}
        keywords={detail.content
          ? [...detail.content.keywords, 'GEO优化案例', '前排科技GEO'].join(',')
          : `${detail.title},GEO优化案例,AI搜索优化,前排科技GEO`}
        structuredData={structuredData}
      />

      <section className="relative overflow-hidden bg-[#090B10] px-6 py-16 text-white md:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_16%,rgba(216,58,64,0.28),transparent_35%)]" />
        <div className="absolute inset-0 opacity-25 [background-image:linear-gradient(rgba(216,58,64,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(216,58,64,0.08)_1px,transparent_1px)] [background-size:46px_46px]" />
        <div className="relative mx-auto max-w-[1100px] text-center">
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight md:text-5xl">
            {detail.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-300">
            <span className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-[#E1676C]" />
              {detail.publishedAt}
            </span>
            <span className="flex items-center gap-2">
              <UserRound className="h-4 w-4 text-[#E1676C]" />
              {detail.author}
            </span>
          </div>
        </div>
      </section>

      <main className="mx-auto flex w-full max-w-[1400px] items-start gap-10 px-4 py-12 md:px-6 md:py-16">
        <aside className="hidden w-[280px] shrink-0 lg:block">
          <div className="sticky top-28 space-y-6">
            {detail.content && (
              <nav className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" aria-label="案例目录">
                <h2 className="mb-4 text-lg font-black text-slate-900">案例目录</h2>
                <ol className="space-y-3 text-sm font-medium text-slate-600">
                  {[
                    ['企业概况', '#case-overview'],
                    ['优化前渠道', '#case-channels'],
                    ['核心痛点', '#case-pain-points'],
                    ['优化方案', '#case-strategy'],
                    ['优化结果', '#case-results'],
                    ['实战逻辑', '#case-takeaways'],
                  ].map(([label, href], index) => (
                    <li key={href}>
                      <a href={href} className="flex items-center gap-3 transition-colors hover:text-[#B7262D]">
                        <span className="text-xs font-black text-[#C92E35]">{String(index + 1).padStart(2, '0')}</span>
                        {label}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            <div className="rounded-2xl border border-[#D83A40]/10 bg-white p-6 text-center shadow-[0_16px_40px_rgba(88,12,18,0.10)]">
            <div className="flex flex-col items-center">
              <div className="mb-4 h-16 w-16 overflow-hidden rounded-full border-2 border-[#D83A40]/20 shadow-[0_8px_20px_rgba(183,38,45,0.14)]">
                <img src={teamImg1} alt="前排GEO-胡老师" className="h-full w-full object-cover" />
              </div>
              <h3 className="mb-2 flex items-center gap-2 text-xl font-bold text-slate-900">
                <User className="h-5 w-5 text-[#C92E35]" />
                前排GEO-胡老师
              </h3>
              <p className="flex items-center gap-2 text-sm font-medium text-[#B7262D]">
                <Award className="h-4 w-4" />
                AI搜索合规优化专家
              </p>
            </div>

            <div className="my-6 border-t border-[#D83A40]/10" />

            <div className="mb-6 flex flex-col items-center">
              <div className="mb-4 h-48 w-48 overflow-hidden rounded-xl border-2 border-slate-100 bg-white p-2 shadow-md">
                <img src={wechatImg} alt="微信二维码" className="h-full w-full object-contain" />
              </div>
              <p className="flex items-center gap-2 text-sm text-slate-600">
                <QrCode className="h-4 w-4 text-[#C92E35]" />
                扫码添加微信
              </p>
            </div>

            <div className="my-6 border-t border-[#D83A40]/10" />

            <div className="flex items-center justify-center gap-3">
              <Phone className="h-5 w-5 text-[#C92E35]" />
              <span className="text-lg font-bold text-slate-900">15871690626</span>
            </div>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <nav className="mb-6 text-sm text-slate-500" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-[#B7262D]">首页</Link>
            <span className="mx-2">/</span>
            <Link to="/cases" className="transition-colors hover:text-[#B7262D]">GEO成功案例</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-800">{detail.title}</span>
          </nav>

          {detail.content && <CaseStudyContent content={detail.content} />}

          {detail.showOriginalImage !== false && <section className={`${detail.content ? 'mt-8' : ''} overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_48px_rgba(88,12,18,0.10)]`}>
            {detail.content ? (
              <details>
                <summary className="cursor-pointer list-none px-6 py-5 font-black text-slate-900 transition-colors hover:bg-slate-50 md:px-8">
                  <span className="flex items-center justify-between gap-4">
                    查看原始案例长图
                    <span className="text-sm font-medium text-[#B7262D]">点击展开</span>
                  </span>
                </summary>
                <div className="border-t border-slate-200">
                  {!imageLoaded && (
                    <div className="flex min-h-[50vh] items-center justify-center bg-[linear-gradient(135deg,#faf7f7_0%,#fff_45%,#f8f0f0_100%)] text-center">
                      <div className="space-y-3 px-6">
                        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-[#E9C9CB] border-t-[#C92E35]" />
                        <p className="text-sm font-medium text-slate-500">案例长图加载中，请稍候…</p>
                      </div>
                    </div>
                  )}
                  <OptimizedImage
                    src={detail.detailImage}
                    alt={detail.imageAlt}
                    className={`block h-auto w-full ${imageLoaded ? 'opacity-100' : 'pointer-events-none absolute opacity-0'}`}
                    onLoad={() => setImageLoaded(true)}
                  />
                </div>
              </details>
            ) : (
              <>
                {!imageLoaded && (
                  <div className="flex min-h-[70vh] items-center justify-center bg-[linear-gradient(135deg,#faf7f7_0%,#fff_45%,#f8f0f0_100%)] text-center">
                    <div className="space-y-3 px-6">
                      <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-[#E9C9CB] border-t-[#C92E35]" />
                      <p className="text-sm font-medium text-slate-500">案例长图加载中，请稍候…</p>
                    </div>
                  </div>
                )}
                <OptimizedImage
                  src={detail.detailImage}
                  alt={detail.imageAlt}
                  className={`block h-auto w-full ${imageLoaded ? 'opacity-100' : 'pointer-events-none absolute opacity-0'}`}
                  loading="eager"
                  fetchPriority="high"
                  onLoad={() => setImageLoaded(true)}
                />
              </>
            )}
          </section>}

          {detail.sourceUrl && (
            <p className="mt-6 text-sm leading-6 text-slate-500">
              案例来源：<a href={detail.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-[#B7262D]">{detail.sourceName}</a> · 按本站风格整理
            </p>
          )}

          <div className="mt-10 flex justify-center">
            <Link
              to="/cases"
              className="rounded-lg border border-[#D83A40]/35 bg-white px-8 py-3 font-bold text-[#B7262D] transition-all hover:border-[#D83A40] hover:bg-[#FFF5F5]"
            >
              返回案例列表
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CaseImageDetailPage;
